/**
 * The notification feed's pure rules: a row's shape, which rows belong to one thread, the bell's unread count and
 * how many rows a header bell draws. No Firebase here, on purpose: the home headers' hook and its tests read these
 * without loading the Firestore client (notificationsRepository re-exports every one of them, so its importers are
 * unchanged).
 */

export type FirestoreNotification = {
  id: string;
  title: string;
  message: string;
  timestamp: string; // ISO string for compatibility with existing UI
  read: boolean;
  metadata?: Record<string, any>;
  created_at?: any;
};

/**
 * Per-thread notification badges (2026-09-08): a row that belongs to ONE
 * thread — a routine run, a thread message, anything stamped with a
 * thread_id — is counted by that thread's own badge, not by the account-level
 * bell. The bell's unread count is account-level only.
 */
export function isThreadScopedNotification(n: Pick<FirestoreNotification, 'metadata'>): boolean {
  const meta = n.metadata;
  if (!meta || typeof meta !== 'object') return false;
  // A request waiting on THIS person's decision (catalog v18: someone asked to
  // archive a thread they pinned) is account-level: the bell counts it even
  // though it opens a thread, so it is never buried in one thread's badge.
  if (meta.kind === 'archive_request') return false;
  if (typeof meta.thread_id === 'string' && meta.thread_id.trim()) return true;
  if (meta.type === 'routine_run') return true;
  if (meta.category === 'threadMessage') return true;
  return false;
}

/** The thread a row points at, when it does (id + optional workspace). */
export function notificationThreadLink(
  n: Pick<FirestoreNotification, 'metadata'>,
): { threadId: string; ws: string | null } | null {
  const meta = n.metadata;
  if (!meta || typeof meta !== 'object') return null;
  const threadId = typeof meta.thread_id === 'string' ? meta.thread_id.trim() : '';
  if (!threadId) return null;
  const ws = typeof meta.account_id === 'string' && meta.account_id.trim()
    ? meta.account_id.trim()
    : typeof meta.ws === 'string' && meta.ws.trim() ? meta.ws.trim() : null;
  return { threadId, ws };
}

/** The rows a header bell's list draws (the app header's and both home headers'). */
export const BELL_ROWS = 10;

/** Account-level unread count — the number the bell shows. */
export function countAccountLevelUnread(notifications: readonly FirestoreNotification[]): number {
  let n = 0;
  for (const item of notifications) {
    if (!item.read && !isThreadScopedNotification(item)) n += 1;
  }
  return n;
}
